import { useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	isBusinessDomain,
	isLikelyPersonalDomain,
} from '@/components/signup/utils/is-personal-domain';
import { experiments } from '@/utils/experiment/manifest';
import { useExperiment } from '@/utils/experiment/use-experiment';

import {
	fireNudgeEmailChangedAnalytics,
	fireNudgeShownAnalytics,
} from './recommend-work-email-nudge-analytics';

/**
 * Encapsulates the `recommend-work-email-signup` experiment behaviour for the
 * inline signup input.
 *
 * Enrollment/exposure for this experiment is deferred (the manifest sets
 * `fireExperimentExposure: false`) so that it only counts users who reach the
 * point where the treatment could actually appear — i.e. those whose email is
 * heading toward a known personal / free provider domain. This hook:
 *
 *  - determines eligibility live, as the user types, once the full provider
 *    label is typed (so "ada@gmail" qualifies, but "ada@gmai" does not) —
 *    deliberately independent of the blur-gated `isValid` state so the nudge
 *    appears immediately rather than only after the field loses focus,
 *  - fires the exposure event exactly once for BOTH control and treatment at
 *    the moment the user first becomes eligible (gating on enrollment rather
 *    than the treatment value keeps the A/B comparison valid), and
 *  - reports whether the treatment nudge should be shown, latching it "on" once
 *    eligible so it persists even if the email is later edited or cleared.
 *
 * @param email The current email input value (may be partially typed).
 * @returns `{ showNudge }` — true for the enrolled treatment arm from the moment
 *   the user first becomes eligible onward (it does not turn back off).
 */
export const useRecommendWorkEmailNudge = ({
	email,
}: {
	email: string;
}): { showNudge: boolean } => {
	const experiment = useExperiment(experiments.recommendWorkEmailSignup);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Only treat the user as enrolled once Statsig has actually resolved their
	// arm. `type === 'enrolled'` alone is true as soon as the (synchronous)
	// targeting cohort is known, but the bucketed parameter value can still be
	// resolving — during that window `getExperimentValue` returns undefined and
	// the hook falls back to `defaultValue` (false) with `usingDefault: true`.
	// A comment elsewhere in the codebase puts it succinctly: "usingDefault is
	// equivalent to not being enrolled in Statsig".
	//
	// Gating on `usingDefault === false` avoids a race that would unbalance the
	// experiment: a treatment user whose bucket has not resolved yet would
	// otherwise fire an exposure as the default (control) value before latching
	// the treatment UI. Because this gate is independent of which arm the user
	// is in, it keeps control/treatment exposure symmetric.
	//
	// Narrow to a single resolved-enrolled reference so downstream reads of
	// `value` / `fireExposure` are type-safe (the hook returns a discriminated
	// union whose not-enrolled / indeterminate arms lack those fields).
	const resolvedExperiment =
		experiment.type === 'enrolled' && experiment.usingDefault === false ? experiment : undefined;

	// Eligible = enrollment is fully resolved AND the domain typed so far is a
	// known personal provider. Evaluated on every keystroke — no dependency on
	// blur/validation.
	const isEligible = resolvedExperiment !== undefined && isLikelyPersonalDomain(email);

	// Fire the exposure event exactly once, for both arms, on first eligibility.
	// `resolvedExperiment` is checked here both to guard the one-shot ref and to
	// narrow the discriminated union so `fireExposure` is type-safe (TypeScript
	// does not carry the narrowing from `isEligible`, which is a plain boolean).
	const hasFiredExposureRef = useRef(false);
	useEffect(() => {
		if (isEligible && resolvedExperiment && !hasFiredExposureRef.current) {
			resolvedExperiment.fireExposure();
			hasFiredExposureRef.current = true;
		}
	}, [isEligible, resolvedExperiment]);

	// Show the nudge for the enrolled treatment arm once eligible — and then keep
	// it shown. The nudge intentionally "latches": once the user's email is
	// confidently personal we surface the recommendation and leave it in place
	// even if they subsequently edit or clear the field, so it doesn't flicker
	// away mid-correction.
	const hasShownNudgeRef = useRef(false);
	if (isEligible && resolvedExperiment !== undefined && resolvedExperiment.value === true) {
		hasShownNudgeRef.current = true;
	}
	const showNudge = hasShownNudgeRef.current;

	// Fire the `recommendWorkEmailNudge shown` UI event exactly once, the first
	// time the treatment nudge is surfaced. Analysis uses this to separate users
	// who actually saw the nudge (and started on a personal domain) from those
	// merely enrolled.
	const hasFiredShownRef = useRef(false);
	useEffect(() => {
		if (showNudge && !hasFiredShownRef.current) {
			hasFiredShownRef.current = true;
			fireNudgeShownAnalytics({ createAnalyticsEvent });
		}
	}, [showNudge, createAnalyticsEvent]);

	// Fire the `recommendWorkEmailNudge emailChanged` track event exactly once,
	// when a user who was shown the nudge enters a *business* email. This is the
	// direct "converted to a work email after the nudge" signal.
	//
	// The guard is a positive business-domain check (`isBusinessDomain`), not a
	// generic "email changed" check: it fires only when the address is both a
	// complete/valid email AND on a non-personal domain. So switching from one
	// personal provider to another (gmail -> yahoo), or partially typing a
	// business domain, does NOT fire — only a genuine move onto a business
	// domain does. Gated on `showNudge` so it only counts users who were
	// actually nudged.
	const hasFiredEmailChangedRef = useRef(false);
	useEffect(() => {
		if (showNudge && !hasFiredEmailChangedRef.current && isBusinessDomain(email)) {
			hasFiredEmailChangedRef.current = true;
			fireNudgeEmailChangedAnalytics({ createAnalyticsEvent });
		}
	}, [showNudge, email, createAnalyticsEvent]);

	return { showNudge };
};
